/**
 * ============================================================================
 * `"Enterprise HRMS Platform (SFT Evolve)"` → brand and descriptor.
 * ============================================================================
 *
 * A PURE DERIVATION OVER `products.json`, NOT A CONTENT CHANGE. The canonical
 * `name` stays whole in the layer; this is presentation deciding which half of
 * it leads. A reader on /products is looking for "SFT Evolve", not "Enterprise
 * HRMS Platform" — and one entry runs 88 characters, because the SFT Warehouse
 * suite lists four products inside its parentheses, so set as a single heading
 * it dominates its card and buries the product's actual name.
 *
 * ----------------------------------------------------------------------------
 * WHY IT IS A MODULE AND NOT A THIRD LOCAL COPY
 * ----------------------------------------------------------------------------
 * It was written twice — `splitName` in `Products.tsx` and `brandOf` in
 * `ProductsTeaser.tsx`, the same four lines with different return types — and a
 * third consumer (`AiInnovation.tsx`, naming the product an achievement shipped
 * in) is what made the duplication worth removing rather than noting. That is
 * the `dateLabels.ts` precedent exactly: extracted the moment a third copy of
 * the month array was about to be written, not the moment the second appeared.
 *
 * D-061 is the rule — material has one home — and D-098 is what the repository
 * paid to learn it in CSS: a rule kept in a second place because it was
 * harmless there beat the shared one on nothing but bundle order the day its
 * content arrived, and rendered two spines of one page at different sizes.
 * Two parsers of one string format drift the same way and more quietly, because
 * nothing renders the disagreement — one surface simply starts saying a
 * different name for the same product.
 *
 * A NAME WITH NO PARENTHETICAL KEEPS ITS WHOLE NAME AS THE BRAND, and the
 * descriptor is `null` rather than an empty string, so a caller cannot render a
 * blank sub-line by forgetting to check. Every entry in `products.json` carries
 * one today; that is not a property the layer promises.
 */

/** The two halves of a product name. `descriptor` is `null` when there is none. */
export interface ProductNameParts {
	/** The parenthetical — what the product is called. */
	brand: string;
	/** What it is, or `null` if the name carries no parenthetical. */
	descriptor: string | null;
}

/** `"Enterprise HRMS Platform (SFT Evolve)"` → `{ brand: "SFT Evolve", descriptor: "Enterprise HRMS Platform" }`. */
export const splitProductName = (name: string): ProductNameParts => {
	const open = name.indexOf(" (");
	if (open === -1 || !name.endsWith(")")) return { brand: name, descriptor: null };
	return { brand: name.slice(open + 2, -1), descriptor: name.slice(0, open) };
};

/** `"Enterprise Compliance Platform (SFT Compliance Hub)"` → `"SFT Compliance Hub"`. */
export const productBrand = (name: string): string => splitProductName(name).brand;
